import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { onHasUnsavedChanges } from 'store/helpers/helpersSlice';
import { enUSFormatExcludingTime, lenientParse } from 'services/items';
import mappedItemTypes from 'services/items/mappedItemTypes';
import {
    setSelectedBookId,
    setSelectedItemTypeId,
    changeCurrentStep,
    setCreatedItemId,
    setSelectedAutoForm,
    setSelectedItemTypeName,
    setItemFormData,
    setGenericAssetTemplates,
    reset,
    setHasGenericAssetTimedOut,
    addRenewableProjectDate,
    removeRenewableProjectDate,
} from 'store/itemCreationWizard/itemCreationWizardSlice';
import { getPricingPoints } from 'store/item/itemApi';
import { getWeatherZones } from 'store/itemCreationWizard/itemCreationWizardApi';
import { format } from 'date-fns';

const useItemCreationWizard = () => {
    const dispatch = useDispatch();

    const {
        selectedBookId,
        selectedItemTypeId,
        selectedAutoForm,
        selectedItemTypeName,
        createdItemId,
        currentStep,
        steps,
        itemFormData,
        genericAssetTemplates,
        hasGenericAssetTimedOut,
        weatherZones,
        renewableProjectDates,
        itemTypesWithWeatherZones,
    } = useSelector((state) => state.itemCreationWizard);

    const updateItemTypeId = useCallback(
        (itemTypeId) => {
            dispatch(setSelectedItemTypeId(itemTypeId));
        },
        [dispatch]
    );

    const updateBookId = useCallback(
        (bookId) => {
            dispatch(setSelectedBookId(bookId));
        },
        [dispatch]
    );

    const updateCreatedItemId = useCallback(
        (itemId) => {
            dispatch(setCreatedItemId(itemId));
        },
        [dispatch]
    );

    const updateItemTypeName = useCallback(
        (itemTypeName) => {
            dispatch(setSelectedItemTypeName(itemTypeName));
        },
        [dispatch]
    );

    const updateItemFormData = useCallback(
        (formData) => {
            dispatch(setItemFormData(formData));
        },
        [dispatch]
    );

    const updateAutoForm = useCallback(
        (autoForm) => {
            dispatch(setSelectedAutoForm(autoForm));
        },
        [dispatch]
    );

    const updateAvailableWeatherZones = useCallback(() => {
        dispatch(getWeatherZones());
    }, [dispatch]);

    const moveToItemFormStep = useCallback(() => {
        //update the itemFormData as it may have gotten out of sync with the selected bookId and itemTypeId values
        dispatch(setItemFormData({ ...itemFormData, bookId: selectedBookId, itemTypeId: selectedItemTypeId }));

        dispatch(changeCurrentStep(steps.itemFormStep));
    }, [dispatch, steps, itemFormData, selectedBookId, selectedItemTypeId]);

    const moveToItemPageStep = useCallback(() => {
        dispatch(changeCurrentStep(steps.moveToItemPageStep));
    }, [dispatch, steps]);

    const moveToGenericItemPage = useCallback(
        (timeOutHit) => {
            dispatch(setHasGenericAssetTimedOut(timeOutHit));
            dispatch(changeCurrentStep(steps.moveToItemPageStep));
        },
        [dispatch, steps]
    );

    const moveToSubItemDecisionStep = useCallback(() => {
        dispatch(changeCurrentStep(steps.subItemMethodDecisionStep));
    }, [dispatch, steps]);

    const moveToBasisSetupStep = useCallback(() => {
        dispatch(changeCurrentStep(steps.basisSetupStep));
    }, [dispatch, steps]);

    const moveToGenericAssetStep = useCallback(
        (templates) => {
            dispatch(setGenericAssetTemplates(templates));
            dispatch(changeCurrentStep(steps.subItemGenericAssetStep));
        },
        [dispatch, steps]
    );

    const moveToCustomSubItemStep = useCallback(() => {
        switch (selectedItemTypeId) {
            case mappedItemTypes.BATTERY_STORAGE.id:
                dispatch(changeCurrentStep(steps.subItemBatteryCustomStep));
                break;
            case mappedItemTypes.WIND_ASSET.id:
                updateAvailableWeatherZones();
                dispatch(changeCurrentStep(steps.subItemRenewablesCustomStep));
                break;
            default:
                break;
        }
    }, [dispatch, steps, selectedItemTypeId, updateAvailableWeatherZones]);

    const resetWizard = useCallback(() => {
        dispatch(reset());
    }, [dispatch]);

    const onFormHasUnsavedChanges = useCallback(
        (isFormDirty) => {
            dispatch(onHasUnsavedChanges(isFormDirty));
        },
        [dispatch]
    );

    const updatePricingPoints = useCallback(() => {
        dispatch(getPricingPoints());
    }, [dispatch]);

    const createRenewableProjectDate = useCallback(
        (subItemId, onlineDate = null, offlineDate = null) => {
            dispatch(
                addRenewableProjectDate({
                    subItemId: subItemId,
                    enteredOnlineDate: onlineDate ? format(lenientParse(onlineDate), enUSFormatExcludingTime) : null,
                    enteredOfflineDate: offlineDate ? format(lenientParse(offlineDate), enUSFormatExcludingTime) : null,
                })
            );
        },
        [dispatch]
    );

    const deleteRenewableProjectDate = useCallback(
        (subItemId) => {
            dispatch(removeRenewableProjectDate(subItemId));
        },
        [dispatch]
    );

    const itemTypeId = useMemo(() => selectedItemTypeId, [selectedItemTypeId]);
    const itemTypeName = useMemo(() => selectedItemTypeName, [selectedItemTypeName]);
    const autoForm = useMemo(() => selectedAutoForm, [selectedAutoForm]);
    const newItemId = useMemo(() => createdItemId, [createdItemId]);
    const bookId = useMemo(() => selectedBookId, [selectedBookId]);
    const formDataForItemCreation = useMemo(() => itemFormData, [itemFormData]);
    const genericTemplates = useMemo(() => genericAssetTemplates, [genericAssetTemplates]);
    const isOnBasisSetupStep = useMemo(() => currentStep === steps.basisSetupStep, [currentStep, steps]);
    const genericTimeoutHit = useMemo(() => hasGenericAssetTimedOut, [hasGenericAssetTimedOut]);
    const availableWeatherZones = useMemo(() => weatherZones, [weatherZones]);
    const projectOnOffDates = useMemo(() => renewableProjectDates, [renewableProjectDates]);
    const weatherZoneAssignableItemTypeIds = useMemo(() => itemTypesWithWeatherZones, [itemTypesWithWeatherZones]);

    return useMemo(
        () => ({
            itemTypeId,
            autoForm,
            newItemId,
            bookId,
            itemTypeName,
            formDataForItemCreation,
            genericTemplates,
            genericTimeoutHit,
            availableWeatherZones,
            isOnBasisSetupStep,
            projectOnOffDates,
            weatherZoneAssignableItemTypeIds,
            updateItemTypeId,
            updateBookId,
            updateCreatedItemId,
            updateItemFormData,
            updateAutoForm,
            updateItemTypeName,
            updateAvailableWeatherZones,
            moveToItemFormStep,
            moveToSubItemDecisionStep,
            moveToBasisSetupStep,
            moveToGenericAssetStep,
            moveToCustomSubItemStep,
            moveToItemPageStep,
            moveToGenericItemPage,
            onFormHasUnsavedChanges,
            resetWizard,
            updatePricingPoints,
            deleteRenewableProjectDate,
            createRenewableProjectDate,
        }),
        [
            itemTypeId,
            autoForm,
            newItemId,
            bookId,
            itemTypeName,
            formDataForItemCreation,
            genericTemplates,
            genericTimeoutHit,
            availableWeatherZones,
            isOnBasisSetupStep,
            projectOnOffDates,
            weatherZoneAssignableItemTypeIds,
            updateItemTypeId,
            updateBookId,
            updateCreatedItemId,
            updateItemFormData,
            updateAutoForm,
            updateItemTypeName,
            updateAvailableWeatherZones,
            moveToItemFormStep,
            moveToSubItemDecisionStep,
            moveToBasisSetupStep,
            moveToGenericAssetStep,
            moveToCustomSubItemStep,
            moveToItemPageStep,
            moveToGenericItemPage,
            onFormHasUnsavedChanges,
            resetWizard,
            updatePricingPoints,
            deleteRenewableProjectDate,
            createRenewableProjectDate,
        ]
    );
};

export default useItemCreationWizard;
